import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type ProductUnit = "UNIDAD" | "KG" | "LB" | "LITRO" | "PAQUETE" | "CAJA";

export const PRODUCT_UNIT_LABEL: Record<ProductUnit, string> = {
  UNIDAD: "Unidad",
  KG: "Kilogramo",
  LB: "Libra",
  LITRO: "Litro",
  PAQUETE: "Paquete",
  CAJA: "Caja",
};

export interface ProductCategoryRef {
  id: string;
  name: string;
}

export interface ProductRecord {
  id: string;
  categoryId: string | null;
  category?: ProductCategoryRef | null;
  subcategoryId: string | null;
  subcategory?: ProductCategoryRef | null;
  sku: string;
  barcode: string | null;
  name: string;
  description: string | null;
  brand: string | null;
  presentation: string | null;
  unit: ProductUnit;
  costPrice: string;
  salePrice: string;
  wholesalePrice: string | null;
  distributorPrice: string | null;
  taxRate: string;
  stockQty: string;
  /** Solo viene cuando se filtra por branchId: saldo real de ESA sucursal
   * (tabla product_branch_stock), a diferencia de stockQty que es el
   * agregado de toda la organización. */
  branchStockQty?: string;
  minStock: string;
  maxStock: string | null;
  maxDiscountPercent: string | null;
  primarySupplierId: string | null;
  primarySupplier?: { id: string; name: string } | null;
  warrantyMonths: number | null;
  isActive: boolean;
  /** true si el producto tiene una foto subida (GET /inventory/products/:id/image la sirve). Nunca se expone la ruta interna del bucket S3/MinIO. */
  hasImage: boolean;
  /** Calculado en vivo por el backend (no se guarda): (salePrice - costPrice) / salePrice * 100. Null si salePrice es 0. */
  marginPercent: number | null;
  createdAt: string;
}

export interface ListProductsFilters {
  page?: number;
  pageSize?: number;
  isActive?: boolean;
  lowStock?: boolean;
  search?: string;
  categoryId?: string;
  branchId?: string;
}

export function useProducts(filters: ListProductsFilters = {}) {
  return useQuery({
    queryKey: ["store", "products", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ProductRecord[]>>("/inventory/products", { params: filters });
      return data;
    },
  });
}

export interface CreateProductPayload {
  categoryId?: string | null;
  subcategoryId?: string | null;
  sku: string;
  barcode?: string;
  name: string;
  description?: string;
  brand?: string | null;
  presentation?: string | null;
  unit?: ProductUnit;
  costPrice: number | string;
  salePrice: number | string;
  wholesalePrice?: number | string | null;
  distributorPrice?: number | string | null;
  taxRate?: number | string;
  minStock?: number | string;
  maxStock?: number | string | null;
  maxDiscountPercent?: number | string | null;
  primarySupplierId?: string | null;
  initialStock?: number | string;
  warrantyMonths?: number | string | null;
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateProductPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<ProductRecord>>("/inventory/products", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "products"] }),
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Partial<CreateProductPayload> & { isActive?: boolean }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<ProductRecord>>(`/inventory/products/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "products"] }),
  });
}

/** Sube/reemplaza la foto de un producto. Ver ProductImage.tsx para cómo se muestra (requiere el header Authorization, por eso no se usa un <img src> directo — ver useProductImageUrl). */
export function useUploadProductImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await apiClient.post<ApiSuccessResponse<ProductRecord>>(`/inventory/products/${id}/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "products"] }),
  });
}

export function useDeleteProductImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<ApiSuccessResponse<ProductRecord>>(`/inventory/products/${id}/image`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "products"] }),
  });
}

/** Trae el binario de la foto del producto (requiere el header Authorization -> no sirve un <img src="/api/..."> plano) y devuelve una object URL lista para <img src>. Cacheado por react-query para no volver a pedirla en cada render; ver components/ProductImage.tsx. */
export function useProductImageUrl(productId: string | undefined, hasImage: boolean) {
  return useQuery({
    queryKey: ["store", "product-image", productId],
    queryFn: async () => {
      const response = await apiClient.get(`/inventory/products/${productId}/image`, { responseType: "blob" });
      return URL.createObjectURL(response.data as Blob);
    },
    enabled: !!productId && hasImage,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: false,
  });
}

export function useAdjustProductStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, branchId, quantity, notes }: { id: string; branchId?: string; quantity: number; notes?: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<ProductRecord>>(`/inventory/products/${id}/adjust`, { branchId, quantity, notes });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "products"] }),
  });
}

export type ShrinkageReason = "VENCIDO" | "DANADO" | "ROBO" | "ERROR_CONTEO" | "DERRAME" | "OTRO";

export const SHRINKAGE_REASON_LABEL: Record<ShrinkageReason, string> = {
  VENCIDO: "Vencido",
  DANADO: "Dañado",
  ROBO: "Robo",
  ERROR_CONTEO: "Error de conteo",
  DERRAME: "Derrame",
  OTRO: "Otro",
};

/** Registra una merma (baja de inventario con motivo categorizado) — reutiliza `adjustStock` en el backend, no duplica el punto de escritura de stock (ver inventory.service.ts::registerShrinkage). */
export function useRegisterShrinkage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { productId: string; branchId?: string; quantity: number; reason: ShrinkageReason; notes?: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<ProductRecord>>("/inventory/shrinkage", payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
      queryClient.invalidateQueries({ queryKey: ["reports", "store-shrinkage"] });
    },
  });
}

export interface CategoryRecord {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  parentCategoryId: string | null;
  parentCategory?: { id: string; name: string } | null;
}

export function useCategories(isActive?: boolean) {
  return useQuery({
    queryKey: ["store", "categories", isActive],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<CategoryRecord[]>>("/inventory/categories", { params: { isActive } });
      return data.data;
    },
  });
}

export interface CreateCategoryPayload {
  name: string;
  description?: string;
  parentCategoryId?: string | null;
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateCategoryPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<CategoryRecord>>("/inventory/categories", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "categories"] }),
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Partial<CreateCategoryPayload> & { isActive?: boolean }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<CategoryRecord>>(`/inventory/categories/${id}`, payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "categories"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/inventory/categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "categories"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

export interface BatchRecord {
  id: string;
  productId: string;
  batchNumber: string | null;
  quantity: string;
  expirationDate: string | null;
  receivedAt: string;
  notes: string | null;
  isActive: boolean;
  supplierId: string | null;
  supplierLotRef: string | null;
  product?: { id: string; name: string; sku: string; unit: ProductUnit };
  supplier?: { id: string; name: string } | null;
}

export function useProductBatches(productId: string | undefined, onlyActive = false) {
  return useQuery({
    queryKey: ["store", "products", productId, "batches", onlyActive],
    enabled: !!productId,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<BatchRecord[]>>(`/inventory/products/${productId}/batches`, { params: { onlyActive } });
      return data.data;
    },
  });
}

export interface CreateBatchPayload {
  batchNumber?: string;
  quantity: number | string;
  expirationDate?: string;
  receivedAt?: string;
  notes?: string;
  supplierId?: string;
  supplierLotRef?: string;
}

export function useCreateBatch(productId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateBatchPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<BatchRecord>>(`/inventory/products/${productId}/batches`, payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

export function useConsumeBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; quantity: number | string; reason: "VENCIDO" | "DANADO" | "DEVUELTO_PROVEEDOR" | "OTRO"; notes?: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<BatchRecord>>(`/inventory/batches/${id}/consume`, payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

export function useExpiringBatches(daysAhead = 30) {
  return useQuery({
    queryKey: ["store", "batches", "expiring", daysAhead],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<BatchRecord[]>>("/inventory/batches/expiring", { params: { daysAhead } });
      return data.data;
    },
  });
}

export interface ImportRowResult {
  row: number;
  sku: string | null;
  status: "created" | "updated" | "error";
  message: string;
}

export interface ImportProductsResult {
  totalRows: number;
  created: number;
  updated: number;
  errors: number;
  rows: ImportRowResult[];
}

export function useImportProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await apiClient.post<ApiSuccessResponse<ImportProductsResult>>("/inventory/products/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
      queryClient.invalidateQueries({ queryKey: ["store", "categories"] });
    },
  });
}

/** Descarga la plantilla .xlsx de carga masiva de productos (requiere el header Authorization, por eso no se usa un <a href> simple). */
export async function downloadProductImportTemplate() {
  const response = await apiClient.get("/inventory/products/import/template", { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = "plantilla-productos.xlsx";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export type StockCountStatus = "ABIERTA" | "APLICADA" | "CANCELADA";

export interface StockCountSummary {
  id: string;
  branchId: string;
  branchName: string | null;
  status: StockCountStatus;
  notes: string | null;
  appliedAt: string | null;
  createdAt: string;
  itemCount: number;
  countedCount: number;
  itemsWithVarianceCount: number;
}

export interface StockCountItemRecord {
  id: string;
  productId: string;
  systemQty: string;
  countedQty: string | null;
  notes: string | null;
  product?: { id: string; name: string; sku: string; unit: ProductUnit };
}

export interface StockCountDetail {
  id: string;
  branchId: string;
  branch?: { id: string; name: string } | null;
  status: StockCountStatus;
  notes: string | null;
  appliedAt: string | null;
  createdAt: string;
  items: StockCountItemRecord[];
}

export function useStockCounts(branchId?: string) {
  return useQuery({
    queryKey: ["store", "stockCounts", branchId ?? ""],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<StockCountSummary[]>>("/inventory/stock-counts", { params: { branchId: branchId || undefined } });
      return data.data;
    },
  });
}

export function useStockCount(id: string | undefined) {
  return useQuery({
    queryKey: ["store", "stockCounts", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<StockCountDetail>>(`/inventory/stock-counts/${id}`);
      return data.data;
    },
  });
}

export interface CreateStockCountPayload {
  branchId: string;
  notes?: string;
  categoryId?: string;
  productIds?: string[];
}

export function useCreateStockCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateStockCountPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<StockCountSummary>>("/inventory/stock-counts", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "stockCounts"] }),
  });
}

export function useUpdateStockCountItem(stockCountId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, countedQty, notes }: { itemId: string; countedQty: number | string | null; notes?: string | null }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<StockCountItemRecord>>(`/inventory/stock-counts/${stockCountId}/items/${itemId}`, { countedQty, notes });
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "stockCounts", stockCountId] });
      queryClient.invalidateQueries({ queryKey: ["store", "stockCounts"] });
    },
  });
}

export function useApplyStockCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<StockCountSummary>>(`/inventory/stock-counts/${id}/apply`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "stockCounts"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

export function useCancelStockCount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<StockCountSummary>>(`/inventory/stock-counts/${id}/cancel`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "stockCounts"] }),
  });
}

export type PromotionDiscountType = "PERCENTAGE" | "FIXED";

export interface PromotionRecord {
  id: string;
  name: string;
  discountType: PromotionDiscountType;
  value: string;
  productId: string | null;
  categoryId: string | null;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  product?: { id: string; name: string; sku: string } | null;
  category?: { id: string; name: string } | null;
}

export function usePromotions(isActive?: boolean) {
  return useQuery({
    queryKey: ["store", "promotions", isActive],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<PromotionRecord[]>>("/inventory/promotions", { params: { isActive } });
      return data.data;
    },
  });
}

export interface CreatePromotionPayload {
  name: string;
  discountType: PromotionDiscountType;
  value: number | string;
  productId?: string;
  categoryId?: string;
  startDate?: string;
  endDate?: string;
}

export function useCreatePromotion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePromotionPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<PromotionRecord>>("/inventory/promotions", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "promotions"] }),
  });
}

export function useUpdatePromotion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: { id: string } & Partial<Omit<CreatePromotionPayload, "productId" | "categoryId">> & { productId?: string | null; categoryId?: string | null; isActive?: boolean }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<PromotionRecord>>(`/inventory/promotions/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "promotions"] }),
  });
}

export function useDeletePromotion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/inventory/promotions/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "promotions"] }),
  });
}

export interface SupplierRecord {
  id: string;
  name: string;
  taxId: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  isActive: boolean;
}

export function useSuppliers(isActive?: boolean) {
  return useQuery({
    queryKey: ["store", "suppliers", isActive],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<SupplierRecord[]>>("/inventory/suppliers", { params: { isActive } });
      return data.data;
    },
  });
}

export interface CreateSupplierPayload {
  name: string;
  taxId?: string;
  phone?: string;
  email?: string;
  address?: string;
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateSupplierPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<SupplierRecord>>("/inventory/suppliers", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "suppliers"] }),
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Partial<CreateSupplierPayload> & { isActive?: boolean }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<SupplierRecord>>(`/inventory/suppliers/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "suppliers"] }),
  });
}

/** Todos los lotes que un proveedor ha entregado (de cualquier producto) — trazabilidad ante una alerta sanitaria. */
export function useSupplierBatches(supplierId: string | undefined) {
  return useQuery({
    queryKey: ["store", "suppliers", supplierId, "batches"],
    enabled: !!supplierId,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<BatchRecord[]>>(`/inventory/suppliers/${supplierId}/batches`);
      return data.data;
    },
  });
}

// ---------- Sucursales (multi-sucursal) ----------

export interface BranchRecord {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  isMain: boolean;
  isActive: boolean;
}

export function useBranches(isActive?: boolean) {
  return useQuery({
    queryKey: ["store", "branches", isActive],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<BranchRecord[]>>("/inventory/branches", { params: { isActive } });
      return data.data;
    },
  });
}

export interface CreateBranchPayload {
  name: string;
  address?: string;
  phone?: string;
}

export function useCreateBranch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateBranchPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<BranchRecord>>("/inventory/branches", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "branches"] }),
  });
}

export function useUpdateBranch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string } & Partial<CreateBranchPayload> & { isActive?: boolean }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<BranchRecord>>(`/inventory/branches/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["store", "branches"] }),
  });
}

// ---------- Trasladar Productos (multi-sucursal) ----------

export interface StockTransferLineRecord {
  id: string;
  productId: string;
  quantity: string;
  product?: { id: string; sku: string; name: string; unit: ProductUnit };
}

export interface StockTransferRecord {
  id: string;
  notes: string | null;
  createdAt: string;
  sourceBranch?: { id: string; name: string };
  destinationBranch?: { id: string; name: string };
  createdBy?: { id: string; fullName: string } | null;
  lines: StockTransferLineRecord[];
}

export function useStockTransfers() {
  return useQuery({
    queryKey: ["store", "stockTransfers"],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<StockTransferRecord[]>>("/inventory/stock-transfers");
      return data.data;
    },
  });
}

export interface CreateStockTransferPayload {
  sourceBranchId: string;
  destinationBranchId: string;
  notes?: string;
  lines: Array<{ productId: string; quantity: number | string }>;
}

export function useCreateStockTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateStockTransferPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<StockTransferRecord>>("/inventory/stock-transfers", payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", "stockTransfers"] });
      queryClient.invalidateQueries({ queryKey: ["store", "products"] });
    },
  });
}

// ---------- Kardex ----------

export type InventoryMovementType =
  | "COMPRA"
  | "VENTA"
  | "AJUSTE"
  | "INICIAL"
  | "ANULACION"
  | "TRANSFERENCIA_SALIDA"
  | "TRANSFERENCIA_ENTRADA"
  | "DEVOLUCION"
  | "MERMA"
  | "DEVOLUCION_PROVEEDOR";

export const MOVEMENT_TYPE_LABEL: Record<InventoryMovementType, string> = {
  COMPRA: "Compra",
  VENTA: "Venta",
  AJUSTE: "Ajuste manual",
  INICIAL: "Stock inicial",
  ANULACION: "Anulación",
  TRANSFERENCIA_SALIDA: "Traslado (salida)",
  TRANSFERENCIA_ENTRADA: "Traslado (entrada)",
  DEVOLUCION: "Devolución de venta",
  MERMA: "Merma",
  DEVOLUCION_PROVEEDOR: "Devolución a proveedor",
};

export interface KardexMovement {
  id: string;
  movementType: InventoryMovementType;
  quantity: string;
  balanceAfter: string;
  reason?: string | null;
  notes: string | null;
  createdAt: string;
  branch?: { id: string; name: string } | null;
  referenceType?: string | null;
  referenceId?: string | null;
  /** Costo unitario del movimiento — null solo en movimientos de antes de esta columna. */
  unitCost?: string | null;
  totalCost?: string | null;
  createdBy?: { id: string; fullName: string } | null;
}

export interface KardexResult {
  product: { id: string; sku: string; name: string; unit: ProductUnit; stockQty: string };
  movements: KardexMovement[];
}

export interface KardexFilters {
  branchId?: string;
  dateFrom?: string;
  dateTo?: string;
  movementType?: string;
}

export function useKardex(productId: string | null, filters: KardexFilters = {}) {
  return useQuery({
    queryKey: ["store", "kardex", productId, filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<KardexResult>>(`/inventory/products/${productId}/kardex`, { params: filters });
      return data.data;
    },
    enabled: !!productId,
  });
}
